import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectTree from '@app/components/select-tree/SelectTree';
import { NIL as NIL_UUID } from 'uuid';
import { Get, PostFormData } from '../../utils/httpProvider';
import { YEUCAU_API } from '../../utils/apiUrl';
import { messageError, messageToast } from '../../utils/define';
import { linhVucGetAllAction } from '../../store/reducers/linhvuc';
import { loaiYeuCauGetAllAction } from '../../store/reducers/loaiyeucau';
import { nhanVienGetAllAction } from '../../store/reducers/nhanVien';
import { nguoiPhoiHopGetAllAction } from '../../store/reducers/nguoiphoihop';
import { khoaGetAllAction } from '../../store/reducers/khoa';
import { TRANG_THAI_YEU_CAU_OPTIONS, MUC_DO_UU_TIEN } from '../../store/constants';
import { userManager } from '@app/utils/oidc';
import { getInfomationAccountFromToken } from '../../utils/helper';
import { getNguoiDungByAccountId } from '../../store/reducers/nguoidung';
import { LOAI_LOI_OPTIONS, MUC_DO_PHUC_TAP_OPTIONS } from '../../store/constants';
import Editor from '@app/components/editor/Editor';


const getTodayStr = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');       
    return `${year}-${month}-${day}`;
};

const schema = yup.object().shape({
    linhVucId: yup.string().required('Vui lòng chọn lĩnh vực'),
    loaiYeuCauId: yup.mixed().required('Vui lòng chọn loại yêu cầu'),
    khoaIds: yup.array().min(1, 'Vui lòng chọn ít nhất 1 khoa'),
    ngayBatDau: yup.string().required(messageError.emptyMessage),
    linkTicket: yup.string().url('Link ticket không hợp lệ').nullable(),
    noiDung: yup.string().nullable(),
    mucDo: yup.string().required('Vui lòng chọn mức độ'),
    nguoiPhoiHop: yup.array().min(1, 'Vui lòng chọn ít nhất 1 người phối hợp'),
    ngayHoanThanhDuKien: yup.string().nullable(),
    ngayHoanThanhThucTe: yup.string().nullable(),
    status: yup.number().nullable(),
    ghiChu: yup.string().nullable(),
    mucDoPhucTap: yup.string().nullable(),
});

const YeuCauAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const listLinhVuc = useSelector((state) => state.linhvuc.listLinhVuc || []);
    const listLoaiYeuCau = useSelector((state) => state.loaiyeucau.listLoaiYeuCau || []);
    const listKhoa = useSelector((state) => state.khoa.listKhoa || []);
    const listNguoiPhoiHop = useSelector((state) => state.nguoiPhoiHop.listNguoiPhoiHop || []);
    const listNhanVien = useSelector((state) => state.nhanVien.list || []);

    const [isLoading, setIsLoading] = useState(false);
    const [newFiles, setNewFiles] = useState([]);

    // file input ref to reset value when needed
    const fileInputRef = useRef(null);

    // helper to deduplicate files
    const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let v = bytes;
        let i = 0;
        while (v >= 1024 && i < units.length - 1) {
            v = v / 1024;
            i += 1;
        }
        return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    // ⭐ Thông tin người dùng login
    const [accountId, setAccountId] = useState(null);
    const [mappedNguoiDungId, setMappedNguoiDungId] = useState(null);
    const [mappedNguoiDungName, setMappedNguoiDungName] = useState(null);
    const [accountDisplayName, setAccountDisplayName] = useState(null);

    // ⭐ Load thông tin người dùng đăng nhập (giống HoTroAdd)
    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                const user = await userManager.getUser();
                const idFromUser =
                    user?.profile?.sub ||
                    user?.profile?.accountId ||
                    user?.profile?.nameid ||
                    null;

                const nameFromUser =
                    user?.profile?.name ||
                    user?.profile?.preferred_username ||
                    user?.profile?.email ||
                    null;

                if (mounted && idFromUser) {
                    setAccountId(idFromUser);
                    if (nameFromUser) setAccountDisplayName(nameFromUser);

                    try {
                        const payload = await dispatch(getNguoiDungByAccountId(idFromUser)).unwrap();
                        let item = payload;

                        if (Array.isArray(item)) item = item[0];
                        if (item?.data) item = item.data;
                        if (Array.isArray(item)) item = item[0];

                        const foundId =
                            item?.id ||
                            item?.nguoiDungId ||
                            item?.NguoiDungId ||
                            null;

                        const foundName =
                            item?.hoTen ||
                            item?.fullName ||
                            item?.name ||
                            item?.ten ||
                            null;

                        if (foundId) setMappedNguoiDungId(foundId);
                        if (foundName) setMappedNguoiDungName(foundName);
                    } catch { }
                } else {
                    // fallback đọc từ token tự decode
                    const info = getInfomationAccountFromToken();

                    const idToken =
                        info?.sub || info?.accountId || info?.nameid || null;

                    const nameFromToken =
                        info?.name ||
                        info?.preferred_username ||
                        info?.email ||
                        null;

                    if (mounted && idToken) {
                        setAccountId(idToken);
                        if (nameFromToken) setAccountDisplayName(nameFromToken);

                        try {
                            const payload = await dispatch(getNguoiDungByAccountId(idToken)).unwrap();
                            let item = payload;

                            if (Array.isArray(item)) item = item[0];
                            if (item?.data) item = item.data;
                            if (Array.isArray(item)) item = item[0];

                            const foundId =
                                item?.id ||
                                item?.nguoiDungId ||
                                item?.NguoiDungId ||
                                null;

                            const foundName =
                                item?.hoTen ||
                                item?.fullName ||
                                item?.name ||
                                item?.ten ||
                                null;

                            if (foundId) setMappedNguoiDungId(foundId);
                            if (foundName) setMappedNguoiDungName(foundName);
                        } catch { }
                    }
                }
            } catch { }
        })();

        return () => (mounted = false);
    }, [dispatch]);

    // Load list
    useEffect(() => {
        if (!listLinhVuc.length) dispatch(linhVucGetAllAction());
        if (!listLoaiYeuCau.length) dispatch(loaiYeuCauGetAllAction());
        if (!listKhoa.length) dispatch(khoaGetAllAction());
        if (!listNguoiPhoiHop.length) dispatch(nguoiPhoiHopGetAllAction());
        if (!listNhanVien.length) dispatch(nhanVienGetAllAction());
    }, [dispatch, listLinhVuc.length, listLoaiYeuCau.length, listKhoa.length, listNguoiPhoiHop.length, listNhanVien.length]);

    // -------------------------------------------------------
    // Submit
    // -------------------------------------------------------
    const handleCreate = async (values) => {
        // Build FormData to match backend expecting multipart/form-data
        const formData = new FormData();

        // Map fields to PascalCase keys that API expects
        const nguoiDungId = mappedNguoiDungId || accountId || '';
        if (nguoiDungId) formData.append('NguoiDungId', String(nguoiDungId));

        if (values.linhVucId !== undefined && values.linhVucId !== null) formData.append('LinhVucId', String(values.linhVucId));
        if (values.loaiYeuCauId !== undefined && values.loaiYeuCauId !== null && values.loaiYeuCauId !== '') {
            formData.append('LoaiYeuCauId', String(values.loaiYeuCauId));
}
        if (Array.isArray(values.khoaIds)) {
            values.khoaIds.forEach((k, idx) => formData.append(`KhoaIds[${idx}]`, String(k)));
        }

        if (values.ngayBatDau) formData.append('NgayBatDau', String(values.ngayBatDau));
        if (values.linkTicket) formData.append('LinkTicket', String(values.linkTicket));
        if (values.noiDung) formData.append('NoiDung', String(values.noiDung));
        if (values.mucDo) formData.append('MucDo', String(values.mucDo));
        if (values.mucDoPhucTap) formData.append('MucDoPhucTap', String(values.mucDoPhucTap));
        // nguoiPhoiHop (string) and nguoiPhoiHopIds (array)
        if (values.nguoiPhoiHop) formData.append('NguoiPhoiHop', String(values.nguoiPhoiHop));
        if (Array.isArray(values.nguoiPhoiHopIds)) values.nguoiPhoiHopIds.forEach((id, idx) => formData.append(`NguoiPhoiHopIds[${idx}]`, String(id)));

        if (Array.isArray(values.nhanVienPhoiHopIds)) values.nhanVienPhoiHopIds.forEach((id, idx) => formData.append(`NhanVienThucHienIds[${idx}]`, String(id)));

        if (values.ngayHoanThanhDuKien) formData.append('NgayHoanThanhDuKien', String(values.ngayHoanThanhDuKien));
        if (values.ngayHoanThanhThucTe) formData.append('NgayHoanThanhThucTe', String(values.ngayHoanThanhThucTe));
        if (values.tienDo !== undefined && values.tienDo !== null) formData.append('TienDo', String(values.tienDo));
        if (values.status !== undefined && values.status !== null) formData.append('Status', String(values.status));
        if (values.ghiChu) formData.append('GhiChu', String(values.ghiChu));

        // Attach files
        if (Array.isArray(newFiles) && newFiles.length > 0) {
            newFiles.forEach((f) => {
                if (f) formData.append('Files', f);
            });
        }

        try {
            const res = await PostFormData(YEUCAU_API.CREATE, null, formData);

            // Try to parse response body
            let body = null;
            try { body = await res.json(); } catch (e) { body = null; }

            const createdId = body && (body.id || (body.data && body.data.id) || (body.result && body.result.id)) || null;
            if (createdId) {
                setIsLoading(true);
                toast.success(messageToast.messageToastCreateSuccess);
                history.goBack();
            } else {
                // If backend returned no body but status ok, still treat as success
                toast.success(messageToast.messageToastCreateSuccess);
                history.goBack();
            }
        } catch (error) {
            // PostFormData throws parsed JSON error when available
            const msg = (error && error.message) ? error.message : 'Đã có lỗi xảy ra, vui lòng thử lại!';
            toast.error(msg);
        }
    };

    const initialNguoiYeuCauDisplay =
        mappedNguoiDungName ||
        accountDisplayName ||
        '';

    const handleNewFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;

        setNewFiles((prev) => {
            const map = new Map((prev || []).map((f) => [toFileKey(f), f]));
            picked.forEach((f) => map.set(toFileKey(f), f));
            return Array.from(map.values());
        });

        // Clear input to allow re-selecting same file later
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClearFiles = () => {
        setNewFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const getDefaultLinhVucId = () => {
        const his = listLinhVuc.find(
            (x) => x.name?.toLowerCase().includes('his-emr')
        );
        return his ? his.id : '';
    };

    const getDefaultLoaiYeuCauId = () => {
        const targetName = 'yêu cầu từ các đơn vị';

        const defaultLoaiYeuCau =
            listLoaiYeuCau.find(
                (x) => x.name?.trim().toLowerCase() === targetName
            ) ||
            listLoaiYeuCau.find(
                (x) => x.name?.trim().toLowerCase().includes(targetName)
            );

        return defaultLoaiYeuCau ? defaultLoaiYeuCau.id : '';
    };

    const initialValues = useMemo(() => ({
        linhVucId: getDefaultLinhVucId(),
        loaiYeuCauId: getDefaultLoaiYeuCauId(),
        khoaIds: [],
        ngayBatDau: getTodayStr(),
        linkTicket: '',
        noiDung: '',
        mucDo: MUC_DO_UU_TIEN[2].key,
        nguoiPhoiHop: '',
        nguoiPhoiHopIds: [],
        nhanVienPhoiHopIds: [],
        mucDoPhucTap: MUC_DO_PHUC_TAP_OPTIONS[0].key,
        ngayHoanThanhDuKien: '',
        ngayHoanThanhThucTe: '',
        tienDo: 0,
        status: 0,
        ghiChu: '',
    }), [listLinhVuc, listLoaiYeuCau]);

    return (
        <Card>
            <Card.Header as="h5">Thêm mới Yêu Cầu</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={handleCreate}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            {/* Người yêu cầu - auto fill - readonly */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người tạo yêu cầu</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="text"
                                        value={initialNguoiYeuCauDisplay}
                                        disabled
                                        readOnly
                                    />
                                </Col>
                            </Form.Group>

                            {/* Lĩnh vực */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Lĩnh vực</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="linhVucId"
                                        value={values.linhVucId || ''}
                                        onChange={(e) => setFieldValue('linhVucId', Number(e.target.value))}
                                        isInvalid={!!errors.linhVucId}
                                    >
                                        <option value="">-- Chọn lĩnh vực --</option>
                                        {listLinhVuc.map((lv) => (
                                            <option key={lv.id} value={lv.id}>
                                                {lv.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.linhVucId}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Loại yêu cầu */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Loại yêu cầu</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="loaiYeuCauId"
                                        value={values.loaiYeuCauId || ''}
                                        onChange={(e) => setFieldValue('loaiYeuCauId', e.target.value ? Number(e.target.value) : '')}
                                        isInvalid={!!errors.loaiYeuCauId}
                                    >
                                        <option value="">-- Chọn loại yêu cầu --</option>
                                        {listLoaiYeuCau.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.loaiYeuCauId}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Khoa */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Khoa hỗ trợ
                                    {values.khoaIds?.length > 0 && (
                                        <span className="text-primary ms-1">
                                            ({values.khoaIds.length})
                                        </span>
                                    )}</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listKhoa.map((k) => ({
                                            id: k.id,
                                            name: k.name,
                                            parentId: k.parentId || NIL_UUID,
                                        }))}
                                        showSearchBox
                                        Placeholder="Chọn khoa hỗ trợ"
                                        positionExpand="right"
                                        multiSelect={true}
                                        multiValueSelected={values.khoaIds}
                                        onChange={(arr) => setFieldValue('khoaIds', arr.map((x) => x.id))}
                                    />
                                    {errors.khoaIds && (
                                        <div className="invalid-feedback d-block">{errors.khoaIds}</div>
                                    )}
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                {/* Label chung */}
                                <Form.Label column sm={2}>
                                    Thời gian
                                </Form.Label>

                                {/* 3 input date */}
                                <Col sm={10}>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">
                                                    Ngày bắt đầu
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayBatDau"
                                                    value={values.ngayBatDau}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayBatDau}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayBatDau}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">
                                                    Ngày dự kiến hoàn thành
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayHoanThanhDuKien"
                                                    value={values.ngayHoanThanhDuKien}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayHoanThanhDuKien}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayHoanThanhDuKien}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">
                                                    Ngày hoàn thành thực tế
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayHoanThanhThucTe"
                                                    value={values.ngayHoanThanhThucTe}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayHoanThanhThucTe}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayHoanThanhThucTe}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                            </Form.Group>


                            {/* Link Ticket */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Link Ticket</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="text"
                                        name="linkTicket"
                                        value={values.linkTicket}
                                        onChange={handleChange}
                                        isInvalid={!!errors.linkTicket}
                                        placeholder="Nhập link ticket"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.linkTicket}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Nội dung */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Nội dung</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        name="noiDung"
                                        value={values.noiDung}
                                        onChange={handleChange}
                                        isInvalid={!!errors.noiDung}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.noiDung}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Mức độ */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Mức độ</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="mucDo"
                                        value={values.mucDo}
                                        onChange={(e) => setFieldValue('mucDo', e.target.value)}
                                        isInvalid={!!errors.mucDo}
                                    >
                                        <option value="">-- Chọn mức độ --</option>
                                        {MUC_DO_UU_TIEN.map((opt) => (
                                            <option key={opt.key} value={opt.key}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Mức độ phức tạp</Form.Label>
                                <Col sm="10">
                                    <Form.Control as="select" name="mucDoPhucTap" value={values.mucDoPhucTap} onChange={(e) => setFieldValue('mucDoPhucTap', e.target.value)} isInvalid={!!errors.mucDoPhucTap}>
                                        <option value="">-- Chọn mức độ phức tạp --</option>
                                        {MUC_DO_PHUC_TAP_OPTIONS.map(opt => (
                                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Người phối hợp (chọn nhiều giống Khoa) */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người phối hợp</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listNguoiPhoiHop.map((k) => ({
                                            id: k.id,
                                            name: k.name,
                                            parentId: k.parentId || NIL_UUID,
                                        }))}
                                        showSearchBox
                                        Placeholder="Chọn người phối hợp"
                                        positionExpand="right"
                                        multiSelect={true}
                                        multiValueSelected={values.nguoiPhoiHopIds}
                                        onChange={(arr) => setFieldValue('nguoiPhoiHopIds', arr.map((x) => x.id))}
                                    />
                                    {errors.nguoiPhoiHopIds && (
                                        <div className="invalid-feedback d-block">{errors.nguoiPhoiHopIds}</div>
                                    )}
                                </Col>
                            </Form.Group>

                            {/* Người thực hiện (chọn nhiều nhân viên) */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người thực hiện</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listNhanVien.map((k) => ({
                                            id: k.id,
                                            name: k.hoTen,
                                            parentId: k.parentId || NIL_UUID,
                                        }))}
                                        showSearchBox
                                        Placeholder="Chọn người thực hiện"
                                        positionExpand="right"
                                        multiSelect={true}
                                        // use consistent camelCase field used in initialValues and handleCreate
                                        multiValueSelected={values.nhanVienPhoiHopIds}
                                        onChange={(arr) => setFieldValue('nhanVienPhoiHopIds', arr.map((x) => x.id))}
                                    />
                                    {errors.NhanVienThucHienIds && (
                                        <div className="invalid-feedback d-block">{errors.NhanVienThucHienIds}</div>
                                    )}
                                </Col>
                            </Form.Group>

                            {/* Tiến độ */}
                            {/*<Form.Group as={Row} className="mb-3">*/}
                            {/*    <Form.Label className="col-sm-2 col-form-label">Tiến độ (%)</Form.Label>*/}
                            {/*    <Col sm="10">*/}
                            {/*        <Form.Control*/}
                            {/*            type="number"*/}
                            {/*            name="tienDo"*/}
                            {/*            value={values.tienDo}*/}
                            {/*            onChange={handleChange}*/}
                            {/*            isInvalid={!!errors.tienDo}*/}
                            {/*            min={0}*/}
                            {/*            max={100}*/}
                            {/*        />*/}
                            {/*        <Form.Control.Feedback type="invalid">{errors.tienDo}</Form.Control.Feedback>*/}
                            {/*    </Col>*/}
                            {/*</Form.Group>*/}

                            {/* Trạng thái */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Trạng thái</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={values.status}
                                        onChange={(e) => setFieldValue('status', Number(e.target.value))}
                                        isInvalid={!!errors.status}
                                    >
                                        {TRANG_THAI_YEU_CAU_OPTIONS.map((opt) => (
                                            <option key={opt.key} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Ghi chú */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Ghi chú</Form.Label>    
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="ghiChu"
                                        value={values.ghiChu}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            {/* Tệp đính kèm */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Tệp đính kèm</Form.Label>
                                <Col sm="10">
                                    <Form.Control ref={fileInputRef} type="file" multiple onChange={handleNewFilesChange} />

                                    {newFiles.length > 0 && (
                                        <Row className="mt-2">
                                            <Col md={6}>
                                                <div className="border rounded">
                                                    <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                                                        <div className="text-muted">Đã chọn {newFiles.length} file</div>
                                                        <Button type="button" theme="soft" onClick={handleClearFiles}>Bỏ tất cả</Button>
                                                    </div>

                                                    <div className="px-2 pb-1">
                                                        {newFiles.map((f, idx) => (
                                                            <div key={toFileKey(f)} className={`d-flex justify-content-between align-items-center py-2 ${idx < newFiles.length - 1 ? 'border-bottom' : ''}`}>
                                                                <div className="me-3" style={{ minWidth: 0 }}>
                                                                    <div className="fw-semibold text-truncate">{f.name}</div>
                                                                    <div className="text-muted small">{formatBytes(f.size)}</div>
                                                                </div>

                                                                <Button
                                                                    type="button"
                                                                    theme="danger"
                                                                    onClick={() => handleRemoveFile(idx)}
                                                                    style={{ padding: '2px 10px', fontSize: 12, lineHeight: '18px' }}
                                                                >
                                                                    Xóa
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Form.Group>

                            {/* Buttons */}
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup>
                                        <Button type="submit" theme="primary" disabled={isLoading}>
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button theme="secondary" onClick={() => history.goBack()}>
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default YeuCauAdd;
